import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import createFlightApi from 'api/flightOffset';
import createOrderApi, { OrderItem, Metadata, FlightDetails, OrderParams } from 'api/order';
import { omit } from 'lodash-es';
import { showToast } from 'modules/app/toasterSlice';
import { appContextSelector } from 'modules/app/appSlice';
import { currentUserSelector, metadatasSelector } from 'modules/user/userSlice';
import { syncAttribution } from 'modules/user/attributionSlice';
import { singlePorfolioSanityIdSelector, virtualPortfoliosSelector } from './portfolioSelectSlice';
import { AppThunk, RootState } from 'store';
import { getDisplayName } from 'utils/metadata';
import axios, { CancelTokenSource } from 'axios';
import { loadBills } from 'modules/billing/billingSlice';
import { getOrderStatus } from 'pages/shared/CorporateOffset/helpers/getOrderStatus';
import { setTag, tags } from 'utils/tags';
import { fixFloat, roundNumberToTwo } from 'utils/numberFormatter';
import { transactionProductsName } from 'utils/order';
import checkJob from 'utils/checkJob';
import { validateItemPortfolio } from 'pages/shared/CorporateOffset/helpers/validateItemPortfolio';
import { PlaceOrderSummary } from 'api/order';

const CancelToken = axios.CancelToken;
let source: CancelTokenSource = CancelToken.source();

//region types
interface CalculateFromToOffsetParams {
  from: AirportOption;
  to: AirportOption;
  via?: AirportOption;
  travelClass: TravelClass | undefined;
  travellers: number;
  flights: number;
  roundTrip: boolean;
}

interface OffsetFromToWithPrice {
  price?: number;
  vat?: number;
  customerVat?: number;
  currency?: string;
  amount?: number;
  totalKilosCo2?: number;
  distance?: number;
  distanceFromVia?: number;
  distanceViaTo?: number;
  flightPax: number;
  excise?: number;
  customerExcise?: number;
}

interface ByDistanceFlightUpdateValues {
  flightDistance: number;
  travelClass: Option;
  roundTrip: boolean;
  travellers: number;
  kilosCo2: number;
  flightPax: number;
  price?: number;
  customerPrice?: number;
}
interface OffsetInfo {
  orderName?: string;
  country?: Option;
  department?: Option;
  description?: string;
  emissionScope?: Option;
  timeFrameFrom?: Date;
  timeFrameTo?: Date;
  [key: string]: string | Option | Date | undefined | boolean | number;
}
interface OrderPayload {
  number: string;
  date: string;
  name?: string;
  id: string;
  claimCode?: string;
  inviteId?: string;
  billId?: string;
  orderStatus?: string;
}

export interface FlightOffsetState {
  mode?: FlightOffsetMode;
  amount?: number;
  totalKilosCo2?: number;
  totalKilosCo2Avoided?: number;
  totalKilosCo2Offset?: number;
  vat?: number;
  customerVat?: number;
  customerPrice?: number;
  price?: number;
  excise?: number;
  customerExcise?: number;
  currency?: string;
  from?: AirportOption;
  to?: AirportOption;
  via?: AirportOption;
  travellers?: number;
  travelClass?: { label: string; value: TravelClass };
  flights?: number;
  roundTrip?: boolean;
  flightPax?: number;
  distance?: number;
  distanceFromVia?: number;
  distanceViaTo?: number;
  isLoading: boolean;
  selectedPortfolioId?: string;
  orderName?: string;
  orderNumber?: string;
  orderDate?: string;
  orderId?: string;
  country?: Option;
  department?: Option;
  description?: string;
  emissionScope?: Option;
  timeFrameFrom?: Date;
  timeFrameTo?: Date;
  isOrderInfoInvalid?: boolean;
  selectedCustomMetadata?: string[];
  byDistanceFlights: ByDistanceFlight[];
  fileUploading?: boolean;
  fileProcessing?: boolean;
  progressPercentage?: number;
  isUploadingFileCancelled?: boolean;
  attachment?: { storageBlobName: string; type: string };
  fileErrors?: string[];
  fileSuccess?: boolean;
  isPaidByPartner?: boolean;
  customer?: PartnersCustomerOption;
  claimCode?: string;
  inviteId?: string;
  isMultiUpload?: boolean;
  attachmentTempUrl?: string;
  customersCount?: number;
  billId?: string;
  isMultiplierActive?: boolean;
  orderStatus?: string;
  fullOffsetRows?: undefined;
  costCenter?: string;
  poNumber?: string;
  flightsNumber?: FlightsNumber;
  [key: string]:
    | string
    | Option
    | Date
    | number
    | undefined
    | boolean
    | string[]
    | FlightsNumber
    | ByDistanceFlight[]
    | PartnersCustomerOption
    | { storageBlobName: string; type: string };
}

interface OrderResponse {
  orderId: string;
  order?: Order;
  stripePaymentIntentId?: string;
  stripeClientSecret?: string;
  stripePaymentMethodId?: string;
  claimCode?: string;
  inviteId?: string;
  fileUrl?: string;
  billId?: string;
}

//endregion

const initialState: FlightOffsetState = {
  isLoading: false,
  roundTrip: false,
  byDistanceFlights: [],
  isOrderInfoInvalid: true,
};

const tonnesFactor = 1000;

const slice = createSlice({
  name: 'offsetFlight',
  initialState,
  reducers: {
    reset: (state: FlightOffsetState): FlightOffsetState => {
      return { mode: state.mode, ...initialState };
    },
    resetWithoutPortfolioId: (state: FlightOffsetState): FlightOffsetState => {
      return { ...initialState, selectedPortfolioId: state.selectedPortfolioId };
    },
    resetAmountAndPrice: (state: FlightOffsetState): FlightOffsetState => {
      return { ...state, amount: 0, totalKilosCo2: 0, price: 0 };
    },
    resetCustomer: (state: FlightOffsetState): FlightOffsetState => {
      return { ...state, customer: undefined };
    },
    setLoading: (state: FlightOffsetState, action: PayloadAction<boolean>): FlightOffsetState => {
      state.isLoading = action.payload;
      return state;
    },
    setOrderInfo: (state: FlightOffsetState, action: PayloadAction<OffsetInfo>): FlightOffsetState => {
      Object.keys(action.payload).map(key => {
        state[key] = action.payload[key];
      });
      return state;
    },
    setOrderInfoInvalid: (state: FlightOffsetState, action: PayloadAction<boolean>): FlightOffsetState => {
      state.isOrderInfoInvalid = action.payload;
      return state;
    },
    setMode: (state: FlightOffsetState, action: PayloadAction<FlightOffsetMode>): FlightOffsetState => {
      state.mode = action.payload;
      return state;
    },
    setOrder: (state: FlightOffsetState, action: PayloadAction<OrderPayload>): FlightOffsetState => {
      state.orderNumber = action.payload.number;
      state.orderDate = action.payload.date;
      state.orderName = action.payload.name ?? '';
      state.orderId = action.payload.id;
      state.inviteId = action.payload.inviteId;
      state.claimCode = action.payload.claimCode;
      state.billId = action.payload.billId;
      state.orderStatus = action.payload.orderStatus;
      return state;
    },
    setSelectedCustomMetadata: (state: FlightOffsetState, action: PayloadAction<string[]>): FlightOffsetState => {
      state.selectedCustomMetadata = action.payload;
      return state;
    },
    setOffsetWithPrice: (state: FlightOffsetState, action: PayloadAction<OffsetFromToWithPrice>): FlightOffsetState => {
      return { ...state, ...action.payload };
    },
    setOffsetPrice: (
      state: FlightOffsetState,
      action: PayloadAction<{ price: number; vat: number; customerPrice: number; customerVat: number; excise: number; customerExcise: number }>,
    ): FlightOffsetState => {
      state.price = action.payload.price;
      state.vat = action.payload.vat;
      state.customerPrice = action.payload.customerPrice;
      state.customerVat = action.payload.customerVat;
      state.excise = action.payload.excise;
      state.customerExcise = action.payload.customerExcise;
      return state;
    },
    addByDistanceFlightRow: (state: FlightOffsetState, action: PayloadAction<ByDistanceFlight>): FlightOffsetState => {
      state.byDistanceFlights.push(action.payload);
      return state;
    },
    removeByDistanceFlightRow: (state: FlightOffsetState, action: PayloadAction<number>): FlightOffsetState => {
      const filteredFligths = state.byDistanceFlights.filter((_, i) => i !== action.payload);
      state.amount = filteredFligths.reduce((prev, curr) => prev + curr.kilosCo2 / tonnesFactor, 0);
      state.totalKilosCo2 = filteredFligths.reduce((prev, curr) => prev + curr.kilosCo2, 0);
      state.byDistanceFlights = filteredFligths;
      return state;
    },
    setByDistanceFlightLength: (state: FlightOffsetState, action: PayloadAction<{ index: number; value: FlightLengthOption }>): FlightOffsetState => {
      state.byDistanceFlights[action.payload.index].flightLength = action.payload.value;
      return state;
    },
    setByDistanceFlight: (
      state: FlightOffsetState,
      action: PayloadAction<{ index: number; value: ByDistanceFlightUpdateValues }>,
    ): FlightOffsetState => {
      state.byDistanceFlights[action.payload.index] = { ...state.byDistanceFlights[action.payload.index], ...action.payload.value };
      return state;
    },
    setByDistanceTotalAmount: (
      state: FlightOffsetState,
      action: PayloadAction<{ amount: number; kilosCo2: number; index?: number }>,
    ): FlightOffsetState => {
      if (typeof action.payload.index === 'number') {
        const filteredFligths = state.byDistanceFlights.filter((_, i) => i !== action.payload.index);
        state.amount = filteredFligths.reduce((prev, curr) => prev + curr.kilosCo2 / tonnesFactor, action.payload.amount);
        state.totalKilosCo2 = filteredFligths.reduce((prev, curr) => prev + curr.kilosCo2, action.payload.kilosCo2);
      } else {
        const previousAmount = state.amount ?? 0;
        const previousKilosCo2 = state.totalKilosCo2 ?? 0;
        state.amount = previousAmount + action.payload.amount;
        state.totalKilosCo2 = previousKilosCo2 + action.payload.kilosCo2;
      }
      return state;
    },
    resetUploadState: (state: FlightOffsetState, action: PayloadAction<boolean>): FlightOffsetState => {
      if (!action.payload) {
        source.cancel();
      }
      state.fileUploading = action.payload;
      state.fileProcessing = action.payload;
      state.progressPercentage = 0;
      state.isUploadingFileCancelled = true;
      state.fileErrors = [];
      state.attachmentTempUrl = undefined;
      state.attachment = undefined;
      state.fileSuccess = false;
      state.amount = undefined;
      state.totalKilosCo2 = undefined;
      state.customersCount = undefined;
      return state;
    },
    setFileUploading: (state: FlightOffsetState, action: PayloadAction<boolean>): FlightOffsetState => {
      state.fileUploading = action.payload;
      return state;
    },
    setAttachment: (state: FlightOffsetState, action: PayloadAction<{ storageBlobName: string; type: string }>): FlightOffsetState => {
      state.attachment = action.payload;
      return state;
    },
    setAttachmentTempUrl: (state: FlightOffsetState, action: PayloadAction<string>): FlightOffsetState => {
      state.attachmentTempUrl = action.payload;
      return state;
    },
    setCustomersCount: (state: FlightOffsetState, action: PayloadAction<number>): FlightOffsetState => {
      state.customersCount = action.payload;
      return state;
    },
    setFileErrors: (state: FlightOffsetState, action: PayloadAction<string[]>): FlightOffsetState => {
      state.fileErrors = action.payload;
      return state;
    },
    setFileSuccess: (state: FlightOffsetState, action: PayloadAction<boolean>): FlightOffsetState => {
      state.fileSuccess = action.payload;
      return state;
    },
    setUploadTotalAmount: (state: FlightOffsetState, action: PayloadAction<{ amount: number; kilosCo2: number }>): FlightOffsetState => {
      state.amount = action.payload.amount;
      state.totalKilosCo2 = action.payload.kilosCo2;
      return state;
    },
    setIsMultiplierActive: (state: FlightOffsetState, action: PayloadAction<boolean>): FlightOffsetState => {
      state.isMultiplierActive = action.payload;
      return state;
    },
    setProgressPercentage: (state: FlightOffsetState, action: PayloadAction<number>): FlightOffsetState => {
      state.progressPercentage = action.payload;
      return state;
    },
    setFileProcessing: (state: FlightOffsetState, action: PayloadAction<boolean>): FlightOffsetState => {
      state.fileProcessing = action.payload;
      return state;
    },
    resetUploadingFileCancelled: (state: FlightOffsetState): FlightOffsetState => {
      state.isUploadingFileCancelled = false;
      return state;
    },
    setEmissionsAvoided: (state: FlightOffsetState, action: PayloadAction<number>): FlightOffsetState => {
      state.totalKilosCo2Avoided = action.payload;
      return state;
    },
    setEmissionsOffset: (state: FlightOffsetState, action: PayloadAction<number>): FlightOffsetState => {
      state.totalKilosCo2Offset = action.payload;
      return state;
    },
    setFlightsNumber: (state: FlightOffsetState, action: PayloadAction<FlightsNumber>): FlightOffsetState => {
      state.flightsNumber = action.payload;
      return state;
    },
  },
});

const {
  reset,
  resetWithoutPortfolioId,
  setOrderInfo,
  setLoading,
  resetAmountAndPrice,
  setOrderInfoInvalid,
  setSelectedCustomMetadata,
  setMode,
  setOffsetWithPrice,
  setOrder,
  addByDistanceFlightRow,
  removeByDistanceFlightRow,
  setByDistanceFlightLength,
  setByDistanceTotalAmount,
  setByDistanceFlight,
  resetUploadState,
  setFileUploading,
  setFileErrors,
  setFileSuccess,
  setUploadTotalAmount,
  setAttachment,
  setAttachmentTempUrl,
  setCustomersCount,
  setIsMultiplierActive,
  resetCustomer,
  setOffsetPrice,
  setProgressPercentage,
  setFileProcessing,
  resetUploadingFileCancelled,
  setEmissionsAvoided,
  setEmissionsOffset,
  setFlightsNumber,
} = slice.actions;

export {
  reset,
  resetWithoutPortfolioId,
  setOrderInfo,
  resetAmountAndPrice,
  setOrderInfoInvalid,
  setSelectedCustomMetadata,
  setMode,
  removeByDistanceFlightRow,
  setByDistanceFlightLength,
  setByDistanceTotalAmount,
  resetUploadState,
  setIsMultiplierActive,
  resetCustomer,
};
export default slice.reducer;

const saveUsedAirports = (usedAirports: AirportOption[]): AppThunk => (): void => {
  try {
    let savedAirports = JSON.parse(localStorage.getItem('usedAirports') ?? '[]') as AirportOption[];
    savedAirports = savedAirports.filter(s => usedAirports.findIndex(u => u.value === s.value) === -1);
    savedAirports.unshift(...usedAirports);
    const maxSavedAirports = 5;
    savedAirports = savedAirports.slice(0, maxSavedAirports);
    localStorage.setItem('usedAirports', JSON.stringify(savedAirports));
  } catch {
    //err
  }
};

export function generateOrderItemDetailsForFromToMode(flight: FlightOffsetState): FlightDetails {
  const routeLegs: RouteLegs[] = [];

  if (flight.via) {
    routeLegs.push({
      distanceKm: flight.distanceFromVia ?? 0,
      fromAirportCode: flight.from?.airportCode ?? '',
      toAirportCode: flight.via?.airportCode ?? '',
      fromCountryName: flight.from?.countryCode ?? '',
      toCountryName: flight.via?.countryCode ?? '',
      fromCityName: flight.from?.city ?? '',
      toCityName: flight.via?.city ?? '',
    });
    routeLegs.push({
      distanceKm: flight.distanceViaTo ?? 0,
      fromAirportCode: flight.via?.airportCode ?? '',
      toAirportCode: flight.to?.airportCode ?? '',
      fromCountryName: flight.via?.countryCode ?? '',
      toCountryName: flight.to?.countryCode ?? '',
      fromCityName: flight.via?.city ?? '',
      toCityName: flight.to?.city ?? '',
    });
  } else {
    routeLegs.push({
      distanceKm: flight.distance ?? 0,
      fromAirportCode: flight.from?.airportCode ?? '',
      toAirportCode: flight.to?.airportCode ?? '',
      fromCountryName: flight.from?.countryCode ?? '',
      toCountryName: flight.to?.countryCode ?? '',
      fromCityName: flight.from?.city ?? '',
      toCityName: flight.to?.city ?? '',
    });
  }

  return {
    flights: flight.via ? 2 * Number(flight.flights ?? 1) : Number(flight.flights ?? 1),
    passengers: Number(flight.travellers ?? 0),
    roundTrip: flight.roundTrip ?? false,
    travelClass: flight.travelClass?.value ?? ('economy' as TravelClass),
    routeLegs,
  };
}

function generateFlightMetadata(flight: FlightOffsetState): Metadata {
  let flightMetadata;
  if (flight.mode === 'from/to') {
    flightMetadata = {
      travelersCount: Number(flight.travellers ?? 0),
      roundtrip: flight.roundTrip ?? false,
      distanceKm: flight.distance ?? 0,
      fromAirport: flight.from?.airportCode ?? '',
      toAirport: flight.to?.airportCode ?? '',
      fromCountry: flight.from?.countryCode ?? '',
      toCountry: flight.to?.countryCode ?? '',
      fromCity: flight.from?.city ?? '',
      toCity: flight.to?.city ?? '',
      flights: flight.via ? 2 * Number(flight.flights ?? 1) : Number(flight.flights ?? 1),
      passengers: Number(flight.travellers ?? 0),
      travelClass: flight.travelClass?.value ?? ('economy' as TravelClass),
    };
  }

  if (flight.mode === 'byDistance') {
    flightMetadata = {
      travelersCount: flight.byDistanceFlights.reduce((prev, curr) => prev + Number(curr.travellers), 0),
      distanceKm: flight.byDistanceFlights.reduce((prev, curr) => prev + Number(curr.flightDistance), 0),
      flights: flight.byDistanceFlights.reduce((prev, curr) => prev + (curr.roundTrip ? 2 : 1), 0),
      passengers: flight.byDistanceFlights.reduce((prev, curr) => prev + Number(curr.travellers), 0),
    };
  }

  return {
    //@ts-ignore
    flight: flightMetadata,
    order: {
      channel: 'portal-fulloffset',
    },
  };
}

export const getFlightOrderObject = ({
  offsetProps,
  currentUser,
  virtualPortfolios,
  mainPortfolioId,
  isMultiUpload,
  userCurrency,
  customer,
}: {
  offsetProps: FlightOffsetState;
  currentUser: Account | undefined;
  virtualPortfolios: {
    portfolioId: string;
    weight: number;
  }[];
  mainPortfolioId: string | undefined;
  isMultiUpload?: boolean;
  userCurrency: string;
  customer: PartnersCustomerOption | undefined;
}): OrderParams => {
  const { flightPax, distance, mode, byDistanceFlights, isMultiplierActive, from, to, via } = offsetProps;

  const totalKilosCo2 = offsetProps.totalKilosCo2 ?? 0;
  const portfolioId = mainPortfolioId ?? null;

  const isFromToMode = mode === 'from/to';
  const isByDistanceMode = mode === 'byDistance';
  const isUploadMode = mode === 'upload';

  const multiplierMode = currentUser?.partnership?.settings?.portal?.multiplier?.mode;
  const isDiscountMode = multiplierMode === 'Discount';
  const multiplierValue = currentUser?.partnership?.settings?.portal?.multiplier?.value ?? 0;
  const discountValue = multiplierValue / (1 + multiplierValue);
  const discountedOffset = isMultiplierActive && multiplierValue ? (totalKilosCo2 ?? 0) * discountValue : 0;
  const mappedVirtualPortfolios = virtualPortfolios.map(({ portfolioId, weight }) => ({
    id: portfolioId,
    weight: isMultiplierActive && isDiscountMode ? weight * discountValue : weight,
  }));

  let fromToOrderItem: OrderItem | undefined = undefined;

  if (isFromToMode) {
    fromToOrderItem = {
      type: 'impact',
      impact: 'Co2',
      kilosCo2: isDiscountMode ? totalKilosCo2 - discountedOffset : totalKilosCo2,
      portfolio: mainPortfolioId
        ? undefined
        : {
            type: 'Virtual',
            impact: 'co2',
            currency: userCurrency,
            childPortfolios: mappedVirtualPortfolios,
          },
      portfolioId,
      feeFactors: {
        flightPax: flightPax,
        flightDistanceKm: distance ? distance : undefined,
        flight: true,
      },
      taxFactors: {
        airports: [from?.airportCode ?? '', via?.airportCode ?? '', to?.airportCode ?? ''].filter(airport => !!airport),
        customerCountry: customer?.country,
        customerCountrySubdivision: customer?.countrySubdivision,
      },
      details: {
        flight: {
          ...generateOrderItemDetailsForFromToMode(offsetProps),
        },
      },
    };
  }

  let byDistanceFlightItems: OrderItem[] = [];
  if (isByDistanceMode) {
    byDistanceFlightItems = byDistanceFlights.map(item => {
      return {
        type: 'impact',
        impact: 'Co2',
        kilosCo2: isDiscountMode ? item.kilosCo2 - item.kilosCo2 * discountValue : item.kilosCo2,
        portfolio: mainPortfolioId
          ? undefined
          : {
              type: 'Virtual',
              impact: 'co2',
              currency: userCurrency,
              childPortfolios: mappedVirtualPortfolios,
            },
        portfolioId,
        feeFactors: {
          flightPax: item.flightPax,
          flightDistanceKm: item.flightDistance,
          flight: true,
        },
        taxFactors: {
          customerCountry: customer?.country,
          customerCountrySubdivision: customer?.countrySubdivision,
        },
        details: {
          flight: {
            flights: item.roundTrip ? 2 : 1,
            passengers: item.travellers,
            roundTrip: item.roundTrip ?? false,
            travelClass: (item.travelClass?.value as TravelClass) ?? ('economy' as TravelClass),
            routeLegs: [{ distanceKm: item.flightDistance }],
          },
        },
      };
    });
  }

  let uploadOrderItem: undefined | OrderItem = undefined;
  if (isUploadMode) {
    uploadOrderItem = isMultiUpload
      ? {
          type: 'impact',
          impact: 'Co2',
          kilosCo2: isDiscountMode ? totalKilosCo2 - discountedOffset : totalKilosCo2,
          portfolio: mainPortfolioId
            ? undefined
            : {
                type: 'Virtual',
                impact: 'co2',
                currency: userCurrency,
                childPortfolios: mappedVirtualPortfolios,
              },
          portfolioId,
          feeFactors: {
            flight: true,
          },
        }
      : {
          type: 'impact',
          impact: 'Co2',
          kilosCo2: isDiscountMode ? totalKilosCo2 - discountedOffset : totalKilosCo2,
          portfolio: mainPortfolioId
            ? undefined
            : {
                type: 'Virtual',
                impact: 'co2',
                currency: userCurrency,
                childPortfolios: mappedVirtualPortfolios,
              },
          portfolioId,
          feeFactors: {
            flightPax: flightPax,
            flightDistanceKm: distance,
            flight: true,
          },
          taxFactors: {
            customerCountry: customer?.country,
            customerCountrySubdivision: customer?.countrySubdivision,
          },
        };
  }

  let items: OrderItem[] = [];
  if (isFromToMode) {
    items = [fromToOrderItem as OrderItem];
  }
  if (isByDistanceMode) {
    items = byDistanceFlightItems;
  }
  if (isUploadMode) {
    items = [uploadOrderItem as OrderItem];
  }

  const order = {
    currency: userCurrency,
    category: 'Flights',
    items,
  };

  return order;
};

export const calculateFromToOffset = (params: CalculateFromToOffsetParams): AppThunk => async (dispatch, getState): Promise<void> => {
  const context = appContextSelector(getState());
  const flightOffsetApi = createFlightApi(context);
  const { from, to, via, travelClass, travellers, flights, roundTrip } = params;

  if (!from || !to || !travelClass || !travellers || !flights) {
    return;
  }

  dispatch(setLoading(true));

  let offset;
  let partialOffsetFromVia;
  let partialOffsetViaTo;

  try {
    if (via) {
      partialOffsetFromVia = await flightOffsetApi.getFlightOffset({ ...params, from: from.value, to: via.value });
      partialOffsetViaTo = await flightOffsetApi.getFlightOffset({ ...params, from: via.value, to: to.value });

      offset = {
        kilosCo2e:
          partialOffsetFromVia.kilosCo2e && partialOffsetViaTo.kilosCo2e
            ? partialOffsetFromVia?.kilosCo2e + partialOffsetViaTo?.kilosCo2e
            : undefined,
        kilosCo2: partialOffsetFromVia.kilosCo2 + partialOffsetViaTo.kilosCo2,
        distanceKm: partialOffsetFromVia.distanceKm + partialOffsetViaTo.distanceKm,
      };
    } else {
      offset = await flightOffsetApi.getFlightOffset({ travellers, flights, roundTrip, travelClass, from: from.value, to: to.value });
    }

    let multiplicator = roundTrip ? 2 : 1;
    const kilosCo2 = offset.kilosCo2e ?? offset.kilosCo2;

    if (kilosCo2 < 0) {
      dispatch(resetAmountAndPrice());
      dispatch(setLoading(true));
      return;
    }

    multiplicator = via ? multiplicator * 2 : multiplicator;
    const flightPax = multiplicator * Number(params.flights || 1) * Number(params.travellers ?? 1);

    dispatch(
      setOffsetWithPrice({
        amount: kilosCo2 / tonnesFactor,
        totalKilosCo2: kilosCo2,
        distance: offset.distanceKm,
        distanceFromVia: partialOffsetFromVia?.distanceKm,
        distanceViaTo: partialOffsetViaTo?.distanceKm,
        flightPax,
      }),
    );
    dispatch(setLoading(false));
  } catch (err) {
    dispatch(showToast({ variant: 'error', titlei18nKey: 'offset:errorTitle', descriptioni18nKey: 'offset:fetchAmountError' }));
    dispatch(setLoading(false));
  }
};

interface OrderData {
  order?: Order;
  orderId: string;
  fileUrl: string | undefined;
  claimCode: string | undefined;
  inviteId: string | undefined;
  billId: string | undefined;
}

function getFlightOrderData(orderResult: OrderResponse): OrderData {
  const { order, orderId, claimCode, inviteId, billId, fileUrl } = orderResult;
  return {
    order,
    orderId,
    claimCode,
    inviteId,
    billId,
    fileUrl,
  };
}

export const getPriceForOrder = (): AppThunk => async (dispatch, getState): Promise<void> => {
  const flightOffset = getState().flightOffset;
  const { customer, currency, isMultiUpload, mode, byDistanceFlights } = flightOffset;
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const virtualPortfolios = virtualPortfoliosSelector(getState());
  const mainPortfolioId = singlePorfolioSanityIdSelector(getState());
  const orderApi = createOrderApi(context);
  const userCurrency = currency ?? 'USD';
  const customerCurrency = customer?.currency ?? 'USD';
  const customerId = currentUser?.customerId ?? '';
  const partnershipId = currentUser?.partnershipId ?? '';
  const isConnectContext = context === 'connect';

  try {
    const order = getFlightOrderObject({
      offsetProps: flightOffset,
      currentUser,
      virtualPortfolios,
      mainPortfolioId,
      isMultiUpload,
      userCurrency,
      customer,
    });

    validateItemPortfolio(order.items?.[0]);

    const response =
      context === 'connect' ? await orderApi.getQuoteForPartner(order, partnershipId) : await orderApi.getQuoteForCustomer(order, customerId);
    let customerResponse: Order | undefined = undefined;
    let customerPrice = response.totalPrice;
    let customerVat = response.totalVat;
    let customerExcise = response.totalExcise;
    let emissionsAvoided = response.totalKilosCo2Avoided;
    let emissionsOffset = response.totalKilosCo2Offset;
    if (isConnectContext && customer && customerCurrency !== userCurrency) {
      const customerOrder = getFlightOrderObject({
        offsetProps: flightOffset,
        currentUser,
        virtualPortfolios,
        mainPortfolioId,
        isMultiUpload,
        userCurrency: customerCurrency,
        customer,
      });
      customerResponse = await orderApi.getQuoteForPartner(customerOrder, partnershipId);
      customerPrice = customerResponse.totalPrice;
      customerVat = customerResponse.totalVat;
      customerExcise = customerResponse.totalExcise;
      emissionsAvoided = customerResponse.totalKilosCo2Avoided;
      emissionsOffset = customerResponse.totalKilosCo2Offset;
    }

    if (mode === 'byDistance') {
      byDistanceFlights.forEach((item, index) =>
        dispatch(
          setByDistanceFlight({
            index,
            value: {
              ...item,
              price: response.items[index].price,
              customerPrice: customerResponse?.items[index].price,
            },
          }),
        ),
      );
    }

    dispatch(
      setOffsetPrice({
        price: response.totalPrice,
        vat: response.totalVat,
        excise: response.totalExcise,
        customerPrice,
        customerVat,
        customerExcise,
      }),
    );

    dispatch(setEmissionsAvoided(emissionsAvoided ?? 0));
    dispatch(setEmissionsOffset(emissionsOffset ?? 0));
  } catch (err) {
    //@ts-ignore
    if (err.data?.includes('Insufficient supply')) {
      dispatch(
        showToast({
          variant: 'error',
          titlei18nKey: 'offset:corporate.portfolios.errorTitle',
          descriptioni18nKey: 'offset:corporate.portfolios.insufficientSupplyErrorMessage',
        }),
      );
      //@ts-ignore
    } else if (err.data) {
      dispatch(showToast({ variant: 'error', titlei18nKey: 'offset:errorTitle', descriptioni18nKey: 'offset:fetchPriceError' }));
    }
  }
};

export const createFlightOrder = (errorCb?: CallableFunction, successCb?: CallableFunction): AppThunk => async (
  dispatch,
  getState,
): Promise<void> => {
  dispatch(setLoading(true));
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const {
    flightPax,
    byDistanceFlights,
    mode,
    isPaidByPartner,
    customer,
    currency,
    isMultiUpload,
    attachment,
    isMultiplierActive,
    distance,
    price,
    customerPrice,
    poNumber,
    costCenter,
    from,
    to,
    ...restProps
  } = getState().flightOffset;
  const virtualPortfolios = virtualPortfoliosSelector(getState());
  const mainPortfolioId = singlePorfolioSanityIdSelector(getState());
  const metadata = metadatasSelector(getState()) ?? [];
  const portfolioId = mainPortfolioId ?? null;
  const isFromToMode = mode === 'from/to';
  const isByDistanceMode = mode === 'byDistance';
  const isUploadMode = mode === 'upload';
  const orderCurrency = !isPaidByPartner && customer?.currency ? customer.currency ?? 'USD' : currency ?? 'USD';

  const orderApi = createOrderApi(context);
  const { flightOffset, user } = getState();
  const totalKilosCo2 = flightOffset.totalKilosCo2 ?? 0;

  const multiplierValue = currentUser?.partnership?.settings?.portal?.multiplier?.value ?? 0;
  const multiplierMode = currentUser?.partnership?.settings?.portal?.multiplier?.mode;
  const isDiscountMode = multiplierMode === 'Discount';
  const discountValue = multiplierValue / (1 + multiplierValue);
  const discountedOffset = isMultiplierActive && multiplierValue ? (totalKilosCo2 ?? 0) * discountValue : 0;

  const mappedVirtualPortfolios = virtualPortfolios.map(({ portfolioId, weight }) => ({
    portfolioId,
    weight: isMultiplierActive && isDiscountMode ? weight * discountValue : weight,
  }));

  const customMetadata: {
    internalName: string;
    displayName: string;
    value: string | Date;
  }[] = [];

  const customMetadatas = {
    ...omit(restProps, [
      'isLoading',
      'amount',
      'currency',
      'isOrderInfoInvalid',
      'vat',
      'customerVat',
      'excise',
      'customerExcise',
      'orderName',
      'distanceFromVia',
      'distanceViaTo',
      'from',
      'to',
      'via',
      'travelClass',
      'travellers',
      'flights',
      'roundTrip',
      'description',
      'kilosCo2',
      'totalKilosCo2',
      'fileUploading',
      'fileProcessing',
      'progressPercentage',
      'fileErrors',
      'fileSuccess',
      'selectedCustomMetadata',
      'isMultiUpload',
      'attachmentTempUrl',
      'customersCount',
      'inviteId',
      'claimCode',
      'totalKilosCo2Avoided',
      'totalKilosCo2Offset',
      'flightsNumber',
    ]),
  };

  Object.keys(customMetadatas).map(key => {
    let value: string | Date = '';

    if ((flightOffset[key] as Option)?.value) {
      value = (flightOffset[key] as Option)?.value;
    } else {
      value = flightOffset[key] as string | Date;
    }
    if (value) {
      customMetadata.push({
        internalName: key,
        value: value,
        displayName: getDisplayName(metadata, key),
      });
    }
  });

  let fromToOrderItem: OrderItem | undefined = undefined;

  if (isFromToMode) {
    fromToOrderItem = {
      type: 'impact',
      impact: 'Co2',
      kilosCo2: isDiscountMode ? totalKilosCo2 - discountedOffset : totalKilosCo2,
      price: isPaidByPartner || !customer ? price ?? 0 : customerPrice ?? 0,
      ...(mainPortfolioId ? {} : { virtualPortfolios: mappedVirtualPortfolios }),
      portfolioId,
      feeFactors: {
        flightPax: flightPax,
        flightDistanceKm: distance ? distance : undefined,
        flight: true,
      },
      details: {
        flight: {
          ...generateOrderItemDetailsForFromToMode(flightOffset),
        },
      },
      taxFactors: {
        airports: [from?.airportCode ?? '', to?.airportCode ?? ''],
        customerCountry: customer?.country,
        customerCountrySubdivision: customer?.countrySubdivision,
      },
    };
  }

  try {
    let byDistanceFlightItems: OrderItem[] = [];
    if (isByDistanceMode) {
      byDistanceFlightItems = byDistanceFlights.map(item => {
        return {
          type: 'impact',
          impact: 'Co2',
          kilosCo2: isDiscountMode ? item.kilosCo2 - item.kilosCo2 * discountValue : item.kilosCo2,
          price: isPaidByPartner || !customer ? item.price ?? 0 : item.customerPrice ?? 0,
          ...(mainPortfolioId ? {} : { virtualPortfolios: mappedVirtualPortfolios }),
          portfolioId,
          feeFactors: {
            flightPax: item.flightPax,
            flightDistanceKm: item.flightDistance,
            flight: true,
          },
          taxFactors: {
            customerCountry: customer?.country,
            customerCountrySubdivision: customer?.countrySubdivision,
          },
          details: {
            flight: {
              flights: item.roundTrip ? 2 : 1,
              passengers: item.travellers,
              roundTrip: item.roundTrip ?? false,
              travelClass: (item.travelClass?.value as TravelClass) ?? ('economy' as TravelClass),
              routeLegs: [{ distanceKm: item.flightDistance }],
            },
          },
        };
      });
    }

    let uploadOrderItem: undefined | OrderItem = undefined;
    if (isUploadMode) {
      uploadOrderItem = isMultiUpload
        ? {
            type: 'impact',
            impact: 'Co2',
            ...(mainPortfolioId ? {} : { virtualPortfolios: mappedVirtualPortfolios }),
            portfolioId,
          }
        : {
            type: 'impact',
            impact: 'Co2',
            kilosCo2: isDiscountMode ? totalKilosCo2 - discountedOffset : totalKilosCo2,
            price: isPaidByPartner || !customer ? price ?? 0 : customerPrice ?? 0,
            ...(mainPortfolioId ? {} : { virtualPortfolios: mappedVirtualPortfolios }),
            portfolioId,
            feeFactors: {
              flightPax: flightPax,
              flightDistanceKm: distance,
              flight: true,
            },
            taxFactors: {
              customerCountry: customer?.country,
              customerCountrySubdivision: customer?.countrySubdivision,
            },
          };
    }

    const metadata = generateFlightMetadata(flightOffset);

    let items: OrderItem[] = [];
    if (isFromToMode) {
      items = [fromToOrderItem as OrderItem];
    }
    if (isByDistanceMode) {
      items = byDistanceFlightItems;
    }
    if (isUploadMode) {
      items = [uploadOrderItem as OrderItem];
    }

    const order = {
      description: flightOffset.orderName ?? '',
      comment: flightOffset.description ?? '',
      currency: orderCurrency,
      category: 'Flights',
      items,
      metadata: {
        ...metadata,
        custom: customMetadata,
      },
      multiplier: isMultiplierActive ? { value: multiplierValue } : undefined,
    };

    let orderData: Partial<OrderData>;
    if (context === 'connect') {
      const orderBody = {
        ...order,
        facilitated: {
          isFacilitated: true,
          isPaidByPartner: isPaidByPartner ?? true,
        },
        attachments: attachment ? [attachment] : [],
      };

      if (isUploadMode) {
        dispatch(setFileProcessing(true));
        const batchResponse = isMultiUpload
          ? await orderApi.createBatchOrderJobForPartner(orderBody, user.currentAccount?.partnershipId ?? '')
          : await orderApi.createBatchOrderJobForPartner(
              {
                ...orderBody,
                customer: {
                  customerId: customer?.value ?? null,
                },
              },
              user.currentAccount?.partnershipId ?? '',
            );

        const orderResult = await checkJob<PlaceOrderSummary>({
          partnershipId: batchResponse.partnershipId,
          jobId: batchResponse.jobId,
          context,
          module: 'flightOffset',
          setFileProcessingCb: (state: boolean) => dispatch(setFileProcessing(state)),
          setFileUploadingCb: (state: boolean) => dispatch(setFileUploading(state)),
          setProgressPercentageCb: (percentage: number) => dispatch(setProgressPercentage(percentage)),
        });
        const errorMessages = orderResult?.messages?.filter(({ type }) => type.toLowerCase() !== 'information')?.map(m => m.message);
        if (errorMessages?.length > 0) {
          dispatch(setFileErrors(errorMessages));
          dispatch(setFileSuccess(false));
          throw new Error();
        }
        const { facilitatedBatchOrderResult } = orderResult.summary;
        // For multiupload or single with different po numbers or cost centres order property has null value
        orderData = !facilitatedBatchOrderResult.order
          ? { fileUrl: facilitatedBatchOrderResult.fileUrl }
          : getFlightOrderData(facilitatedBatchOrderResult.order);
      } else {
        const orderResult = await orderApi.createForPartner(
          {
            ...orderBody,
            customer: {
              customerId: customer?.value ?? null,
            },
            costCenter: costCenter ?? '',
            poNumber: poNumber ?? '',
          },
          user.currentAccount?.partnershipId ?? '',
        );

        orderData = getFlightOrderData(orderResult);
      }
    } else {
      const orderResult = await orderApi.create(order, user.currentAccount?.customerId ?? '');

      orderData = getFlightOrderData(orderResult);
    }

    const { order: createdOrder, orderId: createdOrderId, billId, claimCode, inviteId, fileUrl } = orderData;
    if (createdOrder) {
      if (context !== 'connect' || isPaidByPartner) {
        setTag({
          ...tags.portalOffset.placeOrder,
          categories: [createdOrder.category],
          transactionId: createdOrderId ?? '',
          transactionTotal: createdOrder.totalPrice,
          currencyCode: createdOrder.currency,
          transactionProducts: createdOrder.items.map(item => ({
            category: createdOrder.category,
            name: `${transactionProductsName.flight} offset`,
            price: roundNumberToTwo(item.price),
            quantity: item.kilosCo2,
            sku: `${transactionProductsName.flight.toLowerCase()}-offset`,
          })),
        });
      }

      dispatch(
        setOrder({
          number: createdOrder.choooseId,
          date: createdOrder.createdDate,
          name: flightOffset.orderName ?? '',
          id: createdOrderId ?? '',
          claimCode,
          inviteId,
          billId,
          orderStatus: createdOrder ? getOrderStatus(createdOrder) : undefined,
        }),
      );
    } else {
      dispatch(setAttachmentTempUrl(fileUrl ?? ''));
    }

    dispatch(syncAttribution());
    dispatch(loadBills());

    if (flightOffset.from && flightOffset.to) {
      if (flightOffset.via) {
        dispatch(saveUsedAirports([flightOffset.from, flightOffset.via, flightOffset.to]));
      } else {
        dispatch(saveUsedAirports([flightOffset.from, flightOffset.to]));
      }
    }
    dispatch(setLoading(false));
    successCb && successCb();
  } catch (err) {
    dispatch(setLoading(false));
    dispatch(showToast({ variant: 'error', titlei18nKey: 'offset:errorTitle', descriptioni18nKey: 'offset:createOrderError' }));
    errorCb && errorCb();
  }
};

export const addNewByDistanceFlightItem = (onSuccess?: CallableFunction): AppThunk => async (dispatch, getState): Promise<void> => {
  dispatch(setLoading(true));
  const context = appContextSelector(getState());
  const flightOffsetApi = createFlightApi(context);

  const item: ByDistanceFlight = {
    flightLength: {
      id: '1',
      value: 'short',
      label: 'Short (< 2h)',
      distance: 1500,
    },
    flightDistance: 1500,
    travelClass: { value: 'economy', label: 'Economy' },
    roundTrip: false,
    travellers: 1,
    kilosCo2: 0,
    flightPax: 1,
  };

  try {
    const result = await flightOffsetApi.getFlightOffset({
      travellers: item.travellers,
      distance: item.flightDistance,
      travelClass: 'economy' as TravelClass,
      roundTrip: item.roundTrip,
    });

    const kilosCo2 = result.kilosCo2e ?? result.kilosCo2;
    item.kilosCo2 = kilosCo2;

    dispatch(addByDistanceFlightRow(item));
    dispatch(setByDistanceTotalAmount({ amount: kilosCo2 / tonnesFactor, kilosCo2 }));
    onSuccess && onSuccess(item);
    dispatch(setLoading(false));
  } catch {
    dispatch(setLoading(false));
  }
};

export const updateByDistanceFlight = (props: { index: number; value: number | Option | boolean; name: string }): AppThunk => async (
  dispatch,
  getState,
): Promise<void> => {
  const { index, value, name } = props;
  dispatch(setLoading(true));
  const context = appContextSelector(getState());
  const flightOffsetApi = createFlightApi(context);
  const { byDistanceFlights } = getState().flightOffset;
  const item = byDistanceFlights[index];

  const travellers = name === 'travellers' ? (value as number) : item.travellers;
  const roundTrip = name === 'roundTrip' ? (value as boolean) : item.roundTrip;
  const flightDistance = name === 'flightDistance' ? (value as number) : item.flightDistance;
  const travelClass = name === 'travelClass' ? (value as Option) : item.travelClass;

  try {
    const result = await flightOffsetApi.getFlightOffset({
      travellers,
      distance: flightDistance,
      travelClass: travelClass.value as TravelClass,
      roundTrip,
    });

    const kilosCo2 = result.kilosCo2e ?? result.kilosCo2;
    const flightPax = Number(travellers ?? 1) * (roundTrip ? 2 : 1);

    dispatch(
      setByDistanceFlight({
        index,
        value: { travellers, flightDistance, travelClass, roundTrip, kilosCo2, flightPax },
      }),
    );
    dispatch(setByDistanceTotalAmount({ amount: kilosCo2 / tonnesFactor, kilosCo2, index }));
    dispatch(setLoading(false));
  } catch {
    dispatch(setLoading(false));
  }
};

export const processFile = (file: File): AppThunk => async (dispatch, getState): Promise<void> => {
  dispatch(setFileSuccess(false));
  dispatch(setFileProcessing(true));
  dispatch(setUploadTotalAmount({ amount: 0, kilosCo2: 0 }));
  dispatch(resetUploadingFileCancelled());
  dispatch(setFlightsNumber({ compensatedFlights: 0, totalFlights: 0 }));
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const flightAPI = createFlightApi(context);
  const currency = getState().flightOffset.currency ?? 'USD';
  const isMultiUpload = getState().flightOffset.isMultiUpload ?? false;

  try {
    source = CancelToken.source();

    let batchResponse;
    if (context === 'connect') {
      batchResponse = isMultiUpload
        ? await flightAPI.createMultiCustomerBatchOrderForPartner(file, source.token, currentUser?.partnershipId ?? 'default', currency)
        : await flightAPI.createBatchOrderForPartner(file, source.token, currentUser?.partnershipId ?? 'default', currency);
    } else {
      batchResponse = await flightAPI.createBatchOrderForCustomer(file, source.token, currentUser?.customerId ?? 'default', currency);
    }

    if (batchResponse.partnershipId && batchResponse.jobId) {
      const response = await checkJob<FlightsProcessingFileSummary>({
        partnershipId: batchResponse.partnershipId,
        jobId: batchResponse.jobId,
        context,
        module: 'flightOffset',
        setFileProcessingCb: (state: boolean) => dispatch(setFileProcessing(state)),
        setFileUploadingCb: (state: boolean) => dispatch(setFileUploading(state)),
        setProgressPercentageCb: (percentage: number) => dispatch(setProgressPercentage(percentage)),
      });
      const processedFile = response?.summary?.processedFile;
      const customersCount = processedFile?.details?.multiCustomerFlights?.customersCount;

      if (processedFile) {
        isMultiUpload && customersCount && dispatch(setCustomersCount(customersCount));
        dispatch(setAttachment(processedFile.attachment));
        dispatch(setAttachmentTempUrl(processedFile.attachmentTempUrl));
        dispatch(setFileErrors([]));
        dispatch(setFileSuccess(true));
        const kilosCo2 = processedFile.priceQuote.items[0].kilosCo2;
        dispatch(setUploadTotalAmount({ amount: kilosCo2 / tonnesFactor, kilosCo2 }));
        dispatch(setFlightsNumber({ compensatedFlights: processedFile.rowsCompensated, totalFlights: processedFile.rows }));
        dispatch(
          setOrderInfo({
            flightPax: processedFile.priceQuote.items[0].feeFactors.flightPax,
            distance: processedFile.priceQuote.items[0].feeFactors.flightDistanceKm,
          }),
        );
      } else {
        if (Array.isArray(response.messages)) {
          const textMessages = response.messages.map(({ message }) => message);
          dispatch(setFileErrors(textMessages));
        } else setFileErrors([]);
      }
    }
  } catch (err) {
    // @ts-ignore
    if (err && err.data) {
      // @ts-ignore
      const { data } = err.data;
      if (data && Array.isArray(data)) {
        dispatch(setFileErrors(data));
        // @ts-ignore
      } else if (typeof err.data === 'string' && err.data.includes('No available supplies found')) {
        //@TODO think how to translate all of the errors here
        dispatch(setFileErrors(['No available supplies found for the default project. Please try changing the default project.']));
      }
    }
    dispatch(setFileUploading(false));
  }
};

export const flightAmountSelector = createSelector(
  (state: RootState) => state.flightOffset,
  state => {
    const { amount } = state;

    return fixFloat(amount ?? 0);
  },
);
